'use client'

import * as React from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/lib/breadcrumb-trail'
import { IconChevronRight } from './icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

/**
 * Etz horizontal navigation (topbar) — code-first, 2026-08-04; no Figma
 * component yet (add to the Etz back-port list, same flow as the reskin).
 * Anatomy: breadcrumb trail left, app-level actions right (icon buttons +
 * user button as a dropdown trigger). App wiring (session, feedback dialog,
 * mobile hamburger) lives in src/components/layout/topbar.tsx.
 */

export function Topbar({ className, children, ...props }: React.ComponentProps<'header'>) {
    return (
        <header
            className={cn(
                'sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-4 lg:px-8',
                className
            )}
            {...props}
        >
            <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
        </header>
    )
}

/**
 * Location trail. Items with an href render as links; the item without one is
 * the current page. The trail truncates from the middle on narrow viewports
 * (each crumb clamps rather than wrapping the bar taller).
 */
export function TopbarBreadcrumb({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
    if (items.length === 0) return null
    return (
        <nav aria-label="Breadcrumb" className={cn('min-w-0 flex-1', className)}>
            <ol className="flex min-w-0 items-center gap-1.5 text-sm">
                {items.map((item, index) => {
                    const isLast = index === items.length - 1
                    return (
                        <li
                            key={`${item.label}-${index}`}
                            className={cn('flex min-w-0 items-center gap-1.5', isLast && 'shrink-0')}
                        >
                            {item.href ? (
                                <Link
                                    href={item.href}
                                    className="truncate text-[var(--text-secondary)] transition-colors hover:text-[var(--text-brand)]"
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <span aria-current="page" className="truncate font-medium text-[var(--text-primary)]">
                                    {item.label}
                                </span>
                            )}
                            {!isLast && (
                                <IconChevronRight className="shrink-0 text-[var(--icon-tertiary)]" aria-hidden="true" />
                            )}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

type TopbarIconButtonProps = {
    label: string
    icon: React.ReactNode
    href?: string
    /** With href: render a plain anchor opening in a new tab (noopener) instead of a Next Link. */
    external?: boolean
    onClick?: () => void
    className?: string
    'data-tour'?: string
}

/**
 * 44px icon action with a built-in tooltip carrying the label. href renders a
 * Link, otherwise a button.
 */
export const TopbarIconButton = React.forwardRef<HTMLElement, TopbarIconButtonProps>(
    function TopbarIconButton({ label, icon, href, external = false, onClick, className, ...rest }, ref) {
        const classes = cn(
            'flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--icon-secondary)] transition-colors',
            'hover:bg-[var(--bg-subtle-hover)] hover:text-[var(--icon-primary)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
            className
        )
        const control = href ? (
            external ? (
                <a
                    ref={ref as React.Ref<HTMLAnchorElement>}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={classes}
                    {...rest}
                >
                    {icon}
                </a>
            ) : (
                <Link ref={ref as React.Ref<HTMLAnchorElement>} href={href} aria-label={label} className={classes} {...rest}>
                    {icon}
                </Link>
            )
        ) : (
            <button
                ref={ref as React.Ref<HTMLButtonElement>}
                type="button"
                onClick={onClick}
                aria-label={label}
                className={classes}
                {...rest}
            >
                {icon}
            </button>
        )
        return (
            <Tooltip>
                <TooltipTrigger asChild>{control}</TooltipTrigger>
                <TooltipContent side="bottom">{label}</TooltipContent>
            </Tooltip>
        )
    }
)

type TopbarUserButtonProps = React.ComponentPropsWithoutRef<'button'> & {
    name: string
    role: string
    initials: string
    imageUrl?: string | null
}

/**
 * Account menu trigger: avatar + name/role stack + chevron. Below md the text
 * collapses and the avatar alone fills a 44px target. forwardRef so it works
 * as a DropdownMenuTrigger asChild.
 */
export const TopbarUserButton = React.forwardRef<HTMLButtonElement, TopbarUserButtonProps>(
    function TopbarUserButton({ name, role, initials, imageUrl, className, ...props }, ref) {
        const avatar = imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- tenant-uploaded avatar, remote host unknown
            <img src={imageUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
        ) : (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-brand-subtle)] text-xs font-semibold text-[var(--text-brand)]">
                {initials}
            </span>
        )

        return (
            <button
                ref={ref}
                type="button"
                aria-label={`Account menu for ${name}`}
                className={cn(
                    'flex h-11 shrink-0 items-center gap-2 rounded-md px-1.5 text-left transition-colors md:px-2',
                    'hover:bg-[var(--bg-subtle-hover)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
                    className
                )}
                {...props}
            >
                {avatar}
                <span className="hidden min-w-0 md:block">
                    <span className="block max-w-40 truncate text-sm font-medium leading-5 text-[var(--text-primary)]">
                        {name}
                    </span>
                    <span className="block max-w-40 truncate text-xs leading-4 text-[var(--text-tertiary)]">
                        {role}
                    </span>
                </span>
                <ChevronDown className="hidden size-4 shrink-0 text-[var(--icon-tertiary)] md:block" aria-hidden="true" />
            </button>
        )
    }
)
